import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BreadcrumbNav } from '../components/BreadcrumbNav';
import { TenantCard } from '../components/TenantCard';
import { getAllTenants } from '../services/portalService'; // We'll need this function or similar
import { TenantListingCard } from '../../../core/types/portal';
import { Filter, Grid, List, MapPin } from 'lucide-react';
import { BRAZILIAN_STATES } from '../data/brazilianStates';

// Mapeamento de rotas para tipos de tenant
const CATEGORY_MAP: Record<string, { label: string; types: string[]; title: string; emoji: string }> = {
  '/pousadas': { 
    label: 'Pousadas', 
    types: ['pousada'], 
    title: 'Melhores Pousadas de Pesca', 
    emoji: '🏡' 
  },
  '/hoteis': { 
    label: 'Hotéis', 
    types: ['hotel'], 
    title: 'Hotéis para Pescadores', 
    emoji: '🏨' 
  },
  '/barcos-hoteis': { 
    label: 'Barcos-Hotéis', 
    types: ['barco-hotel'], 
    title: 'Barcos-Hotéis e Operações Flutuantes', 
    emoji: '🚢' 
  },
  '/guias': { 
    label: 'Guias de Pesca', 
    types: ['guia'], 
    title: 'Guias de Pesca Profissionais', 
    emoji: '🎣' 
  },
  '/agencias': { 
    label: 'Agências', 
    types: ['agencia'], 
    title: 'Agências de Turismo de Pesca', 
    emoji: '✈️' 
  },
  '/influenciadores': { 
    label: 'Influenciadores', 
    types: ['influenciador'], 
    title: 'Influenciadores da Pesca', 
    emoji: '📸' 
  },
  '/associacoes': { 
    label: 'Associações', 
    types: ['associacao'], 
    title: 'Associações de Guias e Turismo', 
    emoji: '🤝' 
  },
  '/pesqueiros': { 
    label: 'Pesqueiros', 
    types: ['pesqueiro'], 
    title: 'Pesque-Pagues e Lagos de Pesca', 
    emoji: '🎣' 
  },
  '/turmas': { 
    label: 'Turmas de Pesca', 
    types: ['turma'], 
    title: 'Turmas e Grupos de Pesca', 
    emoji: '👥' 
  },
};

export const CategoryDirectoryPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const categoryConfig = CATEGORY_MAP[location.pathname] || { 
    label: 'Empresas', types: [], title: 'Empresas de Pesca', emoji: '🏢' 
  };

  const [allTenants, setAllTenants] = useState<TenantListingCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Filtros
  const [selectedState, setSelectedState] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []); // Carrega uma vez on mount

  const loadData = async () => {
    setLoading(true);
    try {
      // TODO: O ideal seria ter um serviço getTenantsByType para não baixar tudo
      const tenantsData = await getAllTenants();
      setAllTenants(tenantsData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtragem local
  const displayedTenants = allTenants.filter(tenant => {
    // 1. Filtro por Categoria (Core da página)
    if (categoryConfig.types.length > 0) {
      const typeMatches = categoryConfig.types.includes(tenant.tenantType);
      if (!typeMatches) return false;
    }

    // 2. Filtro por Estado (Opcional)
    if (selectedState && tenant.location.state !== selectedState) return false;
    
    return true;
  });

  return (
    <div className="min-h-screen bg-brand-midnight flex flex-col">
      {/* Header Content */}
      <div className="bg-white/5 border-b border-white/10">
        <div className="container mx-auto px-6 py-8">
          <BreadcrumbNav 
            items={[
              { label: categoryConfig.label }
            ]}
          />
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-black text-white mb-2 font-display">
                {categoryConfig.emoji} {categoryConfig.title}
              </h1>
              <p className="text-white/40 font-bold uppercase tracking-widest text-xs">
                {loading ? '...' : `${displayedTenants.length} resultados encontrados`}
                {selectedState && ` em ${selectedState}`}
              </p>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                title="Visualização em Grade"
                aria-label="Visualização em Grade"
                className={`p-2 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-brand-gold text-brand-midnight shadow-glow-gold' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
              >
                <Grid size={20} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                title="Visualização em Lista"
                aria-label="Visualização em Lista"
                className={`p-2 rounded-xl transition-all ${viewMode === 'list' ? 'bg-brand-gold text-brand-midnight shadow-glow-gold' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
              >
                <List size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filtros e Listagem */}
      <div className="container mx-auto px-6 py-8 flex-grow">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar de Filtros */}
          <div className="lg:col-span-1">
            <div className="bg-brand-charcoal/30 rounded-2xl p-6 border border-white/10 sticky top-6">
              <h3 className="font-black text-xs uppercase tracking-[0.3em] text-white/30 mb-6 flex items-center gap-2">
                <Filter size={14} className="text-brand-gold" />
                Filtros
              </h3>

              {/* Filtro por Estado */}
              <div className="mb-6">
                <h4 className="font-bold text-xs text-white/50 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <MapPin size={14} />
                  Estado
                </h4>
                <select 
                  className="w-full border border-white/10 bg-white/5 rounded-xl px-3 py-2.5 text-white text-sm focus:ring-2 focus:ring-brand-gold/30 outline-none transition-all"
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  title="Filtrar por Estado"
                  aria-label="Filtrar por Estado"
                >
                  <option value="">Todos os Estados</option>
                  {BRAZILIAN_STATES.map(state => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {/* Grid de Empresas */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold"></div>
                <p className="mt-4 text-white/60 font-medium">Buscando as melhores opções...</p>
              </div>
            ) : displayedTenants.length === 0 ? (
              <div className="bg-white/5 rounded-3xl p-12 text-center border border-white/10 shadow-xl">
                <div className="text-6xl mb-4 grayscale opacity-50">🎣</div>
                <h3 className="text-xl font-black text-white mb-2">
                  Nenhuma empresa encontrada
                </h3>
                <p className="text-white/40 mb-6">
                  Ainda não temos {categoryConfig.label.toLowerCase()} cadastrados(as) com estes filtros.
                </p>
                <button 
                  onClick={() => setSelectedState('')}
                  className="text-brand-gold hover:underline font-bold"
                >
                  Limpar filtros
                </button>
              </div>
            ) : (
              <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 gap-6' : 'space-y-4'}>
                {displayedTenants.map(tenant => (
                  <TenantCard 
                    key={tenant.id} 
                    tenant={tenant}
                    variant={viewMode === 'list' ? 'compact' : 'default'}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
